"use client";

import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WaitlistModal } from "@/components/waitlist-modal";

const navLinks = [
  { label: "Evidence", href: "#evidence" },
  { label: "Preview", href: "#guided-prompt-box" },
];

/* -------------------------------------------------------------------------- */
/*  Component                                                                 */
/* -------------------------------------------------------------------------- */

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background">
      <nav
        className="mx-auto flex h-14 max-w-7xl items-center justify-between px-6 lg:px-8"
        aria-label="Main navigation"
      >
        {/* Brand */}
        <Link
          href="/"
          className="flex items-center gap-2.5 transition-opacity hover:opacity-80"
          aria-label="TrueDemand Holmes home"
        >
          <Image
            src="/images/logo.png"
            alt="TrueDemand Holmes logo"
            width={36}
            height={36}
            className="h-9 w-auto rounded-full"
            priority
          />
          <span className="text-base font-semibold tracking-tight text-foreground">
            TrueDemand{" "}
            <span className="font-serif italic text-accent">Holmes</span>
          </span>
        </Link>

        {/* Desktop Navigation Links + CTA */}
        <div className="hidden items-center gap-1 md:flex">
          {navLinks.map((link) => (
            <Button
              key={link.label}
              variant="ghost"
              size="sm"
              className="text-sm font-medium text-muted-foreground/85 transition-colors hover:text-foreground"
              asChild
            >
              <a href={link.href}>{link.label}</a>
            </Button>
          ))}
          <div className="ml-2">
            <WaitlistModal
              analyticsSource="navbar_desktop"
              triggerText="Apply"
              className="h-9 px-4 py-2 text-sm"
            />
          </div>
        </div>

        {/* Mobile menu button */}
        <button
          type="button"
          className="flex items-center justify-center rounded-md p-2 text-muted-foreground transition-colors hover:text-foreground md:hidden"
          onClick={() => setMobileOpen((v) => !v)}
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
        >
          {mobileOpen ? (
            <X className="size-5" aria-hidden="true" />
          ) : (
            <Menu className="size-5" aria-hidden="true" />
          )}
        </button>
      </nav>

      {/* Mobile Navigation */}
      {mobileOpen && (
        <div
          id="mobile-menu"
          className="border-t border-border bg-background md:hidden"
        >
          <div className="flex flex-col gap-1 px-6 py-4">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground/85 transition-colors hover:bg-accent/10 hover:text-foreground"
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <div className="mt-3">
              <WaitlistModal
                analyticsSource="navbar_mobile"
                triggerText="Apply"
                className="w-full justify-center"
              />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
